import jwtDecode from "jwt-decode";
import { instancePrefix, teamsUrl } from "utils/env";
import {
  getHSVersionName,
  checkIfUserHasDevelopAccess,
} from "utils/helperFunctions";
import { getIsFreeTrialExperimentEnabled } from "containers/Dashboard/helpersFunctions";
import { testIdToTestAbbreviation } from "containers/ScoreRanges/helperFunctions";
import {
  EVENT_DYNAMIC_SCORE_REPORT_VIEW,
  EVENT_PLATFORM_LOGIN_BUTTON_CLICK,
} from "./trackingConstants";

export const segmentIdentifyUser = (
  userData,
  token,
  ftDashboardExperimentAllocation,
  loginType = "password"
) =>
  new Promise((resolve) => {
    // we need the internal property of the token to determine if the user logged in from the intranet
    const jwtDecoded = jwtDecode(token);
    const hsVersionName = userData && getHSVersionName(userData.hsVersion);

    // Determine if the user is in the free trial experiment
    const isFreeTrialExperimentEnabled =
      getIsFreeTrialExperimentEnabled(userData);

    // Set variant using the same logic as in the Dashboard component
    const variant =
      isFreeTrialExperimentEnabled && ftDashboardExperimentAllocation
        ? ftDashboardExperimentAllocation
        : null;

    window.analytics.identify(
      `${userData.userAccountId}${instancePrefix === "AU_" ? "au" : ""}`,
      {
        timeStamp: new Date(),
        type: loginType,
        viEnabled: userData.videoInterviewsEnabled === 1,
        "date subscribed": userData.dateSubscribed,
        "date trial started": userData.dateTrialStarted,
        internal: !!jwtDecoded.internal,
        companyAccountId: userData.companyAccountId,
        plan: hsVersionName,
        "self service eligibility": userData.isBuyNowEligible,
        "quote type": userData.sfQuoteRecordType || "null", // we want to track null, and this is to make sure this field is always included in the payload
        "employee count": userData.numberOfEmployees,
        developEnabled: checkIfUserHasDevelopAccess(userData),
        ...(ftDashboardExperimentAllocation ? { variant } : {}), // Add the variant field based on free trial and feature flag
      },
      null,
      () => {
        resolve();
      }
    );
  });

export const segmentIdentifyGroup = (token, hsVersion) =>
  new Promise((resolve) => {
    window.analytics.group(
      `${token.companyAccountId}${instancePrefix === "AU_" ? "au" : ""}`,
      {
        groupType: "account", // Segment trait "groupType"
        groupValue: token.companyName, // Segment trait "groupValue"
        "company id": `${token.companyAccountId}${
          instancePrefix === "AU_" ? "au" : ""
        }`,
        "plan type": hsVersion,
        "days remaining": token.daysRemaining,
      },
      null,
      () => {
        resolve();
      }
    );
  });

export const trackSSOLogin = (
  userData,
  token,
  hsVersion,
  ftDashboardExperimentAllocation,
  teamingAccessOnly,
  logoutUrl
) => {
  if (!window.analytics) {
    if (teamingAccessOnly) {
      window.location.href = logoutUrl;
    } else {
      return;
    }
    return;
  }

  segmentIdentifyUser(userData, token, ftDashboardExperimentAllocation, "sso");
  segmentIdentifyGroup(userData, hsVersion);
  if (teamingAccessOnly) {
    // handle redirection to teaming after tracking event is triggered
    window.analytics.track(
      "talent insights login",
      {
        "referral source type": "url",
        url: teamsUrl,
      },
      () => {
        window.location.href = logoutUrl;
      }
    );
  } else {
    window.analytics.track("User Login", {});
  }
};

export const trackTimeSpent = (pageName, timeSpent, extraOptions) => {
  window.analytics.track("Time Spent On Page", {
    "Page Name": pageName,
    "Time Spent in Seconds": timeSpent.toFixed(2),
    ...extraOptions,
  });
};

export const trackNavigation = (To) => {
  window.analytics.track("User Navigated", {
    From: window.location.pathname,
    To,
  });
};

export const trackUserPreferenceChanged = (preferenceName, preferenceValue) => {
  window.analytics.track("User Preference Changed", {
    "Preference Name": preferenceName,
    "Preference Value": preferenceValue,
  });
};

export const trackCandidateSorting = (sortCriteria, jobId, extraOptions) => {
  window.analytics.track("results sorted", {
    "job detail id": jobId,
    "sorted by ": sortCriteria,
    ...extraOptions,
  });
};

export const trackDynamicScoreReport = (
  eventType,
  source,
  userAccountId,
  companyAccountId,
  testEventId,
  reportType,
  testId,
  companyStatusConfig
) => {
  const isTM = String(testId).substring(0, 2) === "TM";
  const testName = isTM ? "Test Maker" : testIdToTestAbbreviation[testId];

  let trackingEvent = "Dynamic Score Report ";

  if (testId === "workStyles") {
    trackingEvent += `- Work Styles ${reportType} `;
  } else if (testId) {
    trackingEvent += `- ${testName} ${reportType} `;
  } else if (reportType) {
    trackingEvent += `- ${reportType} `;
  }

  trackingEvent += eventType;

  const trackingData = {
    companyAccountId,
    testEventId,
  };

  if (
    (!reportType && eventType === EVENT_DYNAMIC_SCORE_REPORT_VIEW) ||
    eventType === EVENT_PLATFORM_LOGIN_BUTTON_CLICK
  ) {
    if (source === "email") {
      trackingData.source = source;
    } else if (source === null) {
      trackingData.source = "ATS";
    }

    if (userAccountId) {
      trackingData.userAccountId = userAccountId;
    }
  }

  if (isTM) {
    trackingData.testMakerId = testId;
  }

  if (companyStatusConfig) {
    trackingData.variant = companyStatusConfig;
  }

  window.analytics.track(trackingEvent, trackingData);
};

export const trackScoreRangeSelected = (testName) => {
  window.analytics.track(
    `Dynamic Score Report - ${testName} score range selected`
  );
};

export const trackVIScheduleDemoClicked = (source = "vi-merch-modal") => {
  window.analytics.track("VI Schedule Demo clicked", {
    source,
  });
};

export const trackMGScheduleDemoClicked = (
  version,
  source = "Manager_guide_Adopt_Develop_V1"
) => {
  window.analytics.track("Develop Schedule a Demo Clicked", {
    source,
    version,
  });
};

export const trackVIMerchModalTriggered = (
  source,
  version,
  interviewAbbreviation = "VI"
) => {
  window.analytics.track(`${interviewAbbreviation} Merch Modal triggered`, {
    source,
    version,
  });
};

export const trackMGMerchModalTriggered = (source, version) => {
  window.analytics.track(`Develop Merch Modal triggered`, {
    source,
    version,
  });
};

export const trackSIScheduleDemoClicked = (source) => {
  window.analytics.track("SI Schedule Demo clicked", {
    source,
  });
};

export const trackDSRMerchLearnMoreClicked = (source) => {
  window.analytics.track("DSR Merch Modal - Learn More Clicked", {
    source,
  });
};

export const trackTalentMobilityJobClicked = (
  jobId,
  pipelineStageName,
  companyAccountId
) => {
  window.analytics.track("Talent Mobility Job clicked", {
    pipeline: pipelineStageName,
    jobId,
    companyAccountId,
  });
};

export const trackTalentMobilityCandidateCopied = (
  jobId,
  jobToCopyCandidateId,
  pipelineStageName,
  companyAccountId
) => {
  window.analytics.track("Talent Mobility Candidate Copied", {
    pipeline: pipelineStageName,
    jobId,
    jobToCopyCandidateId,
    companyAccountId,
  });
};

export const trackClickNextFromCreateJob = (
  jobName,
  version,
  addEvaluationTool
) => {
  const { addAssessment, addVideoInterview, addLiveInterview } =
    addEvaluationTool;

  window.analytics.track("Clicked Next from /createJob page", {
    jobName,
    version,
    assessments: addAssessment,
    VI: addVideoInterview,
    LI: addLiveInterview,
  });
};

export const trackCustomTestBatteryCreated = (origin) => {
  window.analytics.track("Custom test battery is created", {
    origin,
  });
};

export const trackSelectTestBattery = (custom, version) => {
  window.analytics.track("Test battery is selected", {
    custom,
    version,
  });
};

export const trackInterviewConfigured = (interviewType) => {
  if (interviewType === "VI") {
    window.analytics.track("VI is configured");
  } else {
    window.analytics.track("LI is configured");
  }
};

export const trackJobCreate = (
  assessments,
  VI,
  LI,
  version,
  fromCustom,
  selectedBatteryTitle,
  selectedVIRecommended,
  selectedLIRecommended
) => {
  window.analytics.track("Job Created", {
    assessments,
    VI,
    LI,
    version,
    "battery name": selectedBatteryTitle,
    ...(assessments && { "recommended battery": !fromCustom }),
    ...(VI && { "Recommended VI template": selectedVIRecommended }),
    ...(LI && { "Recommended LI template": selectedLIRecommended }),
  });
};

export const trackCreateJobButton = (source, version = false) => {
  window.analytics.track("Clicked Create Job Button", {
    source,
    ...(version && { version }),
  });
};

export const trackFTCTAButton = (source) => {
  window.analytics.track("Free Trial WB CTA Clicked", {
    source,
  });
};

export const trackFTUserAssignedCTA = (source) => {
  window.analytics.track("Free Trial User Login", {
    source,
  });
};

export const trackPageViewed = (userAccountId, companyAccountId, pageName) => {
  window.analytics.track("Page Viewed", {
    userAccountId,
    companyAccountId,
    pageName,
  });
};

export const trackAnalyticsFeedback = (
  feedbackResponse,
  feedbackModule,
  companyAccountId,
  userAccountId
) => {
  window.analytics.track("Feedback Submitted", {
    response: feedbackResponse,
    section: feedbackModule,
    companyAccountId,
    userAccountId,
  });
};

export const trackAnalyticsDateFilterChanged = (
  dateRangeSelected,
  companyAccountId
) => {
  window.analytics.track("Date Filter Changed", {
    "date range selected": dateRangeSelected,
    companyAccountId,
  });
};

export const trackAnalyticsExclusionFiltersChanged = (
  source,
  oldValue,
  newValue,
  companyAccountId
) => {
  window.analytics.track(`Exclude ${source} Filter Changed`, {
    "old value": oldValue ? "On" : "Off",
    "new value": newValue ? "On" : "Off",
    companyAccountId,
  });
};

export const trackAnalyticsComparisonBenchmarkChanged = (trackFormData) => {
  window.analytics.track("Different Comparison Option Selected", trackFormData);
};

export const trackMoveCandidateToPostHireModalOpened = (
  testTakerId,
  source
) => {
  window.analytics.track("Move Candidate To PostHire Modal Opened", {
    testTakerId,
    source,
  });
};

export const trackMoveCandidateToPostHire = (createdUserAccountId, source) => {
  window.analytics.track("Candidate Moved To PostHire", {
    createdUserAccountId,
    source,
  });
};

export const trackPostHireV2Login = () => {
  window.analytics.track("Develop By Criteria - User Logged In");
};

export const trackSubscriptionBannerClick = (event) => {
  window.analytics.track(`Clicked ${event}`, {
    source: "subscription-banner",
    version: "blue-gradient",
  });
};

export const trackCheckoutFlowClick = (event, page) => {
  window.analytics.track(`${event}`, {
    source: page,
  });
};

export const trackSmartOnboardingTabWasClicked = () => {
  window.analytics.track("Develop By Criteria - Smart Onboarding Tab Clicked");
};

export const trackSmartOnboardingMerchandizingActionWasClicked = ({
  hasDevelopAccess,
  hasCandidateBeenMoved,
}) => {
  window.analytics.track(
    "Develop By Criteria - Smart Onboarding Merchandizing Action Clicked",
    {
      hasDevelopAccess,
      hasCandidateBeenMoved,
    }
  );
};

export const trackSmartOnboardingDropdownWasClicked = ({
  isSampleTeam,
  hasRealDevelopTeams,
}) => {
  window.analytics.track(
    "Develop By Criteria - Smart Onboarding Dropdown Clicked",
    {
      isSampleTeam,
      hasRealDevelopTeams,
    }
  );
};

export const trackFTExperimentActionCardsClicked = (
  eventName,
  eventAttributes
) => {
  let trackingName;
  let source = "Free Trial Home";

  if (eventName === "si_modal") {
    trackingName = "Interview Education";
    source =
      eventAttributes.surface_area === "dashboard_account_overview"
        ? "Free Trial Dashboard"
        : "Free Trial Home";
  } else if (eventName === "success_stories_modal") {
    trackingName = "See Success Stories";
  } else if (eventName === "develop_modal") {
    trackingName = "Develop Education";
  }

  window.analytics.track(`${trackingName} Triggered`, {
    source,
  });
};

export const trackInterviewEducationTriggered = (surfaceArea) => {
  let source = surfaceArea;

  if (surfaceArea === "analytics_account_overview") {
    source = "analytics_dashboard";
  }
  window.analytics.track("Interview Education Triggered", {
    source,
  });
};

export const trackDevelopEducationTriggered = (surfaceArea) => {
  window.analytics.track("Develop Education Triggered", {
    source: surfaceArea,
  });
};

export const trackVideoInterviewEducationTriggered = (surfaceArea) => {
  let source = surfaceArea;

  if (surfaceArea === "job_creation") {
    source = "create_a_job";
  }
  window.analytics.track("Video Interview Education Triggered", {
    source,
  });
};

export const trackLiveInterviewEducationTriggered = (surfaceArea) => {
  let source = surfaceArea;

  if (surfaceArea === "job_creation") {
    source = "create_a_job";
  }
  window.analytics.track("Live Interview Education Triggered", {
    source,
  });
};

export const trackUpgradeTooltipTriggered = (feature, source) => {
  let trackingName = "";
  if (feature === "structuredInterviewing") {
    trackingName = "Structured Interview";
  } else if (feature === "develop") {
    trackingName = "Develop";
  } else if (source === "manage_menu") {
    // Essentials tooltip is triggered when pro upgrades are locked like landing pages, exit pages, email templates, and rating systems
    trackingName = "Essentials";
  }

  window.analytics.track(`${trackingName} Tooltip Education Triggered`, {
    source,
  });
};

export const trackAnalyticsModalTriggered = (surfaceArea) => {
  window.analytics.track("Analytics Modal Triggered", {
    source: surfaceArea,
  });
};
